import { Box, Checkbox, ListItemText, MenuItem, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { type CollectionDistrict } from '../../../../../models/peerToPeer/Collection'
import { WizardFooter } from './WizardFooter'
import { stepStyles } from './stepStyles'

interface Props {
  districts: CollectionDistrict[]
  onBack: () => void
  onContinue: (districtId: number, buildingIds: number[]) => void
}

export const GettingStartedStep = ({ districts, onBack, onContinue }: Props): JSX.Element => {
  const [districtId, setDistrictId] = useState<number | ''>('')
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<number[]>([])

  const district = districts.find(d => d.districtOrganizationId === districtId)

  return (
    <Box data-testid='p2pGettingStartedStep'>
      <Typography variant='h3'>Getting Started</Typography>
      <Typography variant='body2' sx={stepStyles.intro}>
        Start by selecting the district and then the corresponding buildings you&apos;ll be reporting for.
      </Typography>

      <Typography variant='h4' sx={{ mb: '12px' }}>District</Typography>
      <Typography variant='body1'>Which District are you reporting for?</Typography>
      <Typography variant='caption' color='text.secondary'>This list includes your ISD operating as a District.</Typography>
      <TextField
        select
        fullWidth
        size='small'
        value={districtId}
        onChange={event => { setDistrictId(Number(event.target.value)); setSelectedBuildingIds([]) }}
        sx={{ maxWidth: '480px', mt: '8px' }}
        data-testid='p2pDistrictSelect'
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value='' disabled>Select District</MenuItem>
        {districts.map(d => (
          <MenuItem key={d.districtOrganizationId} value={d.districtOrganizationId}>{d.name}</MenuItem>
        ))}
      </TextField>

      {district != null &&
        <Box sx={{ mt: '24px' }} data-testid='p2pBuildingSelect'>
          <Typography variant='h4' sx={{ mb: '12px' }}>Building</Typography>
          <Typography variant='body1' sx={{ mb: '4px' }}>Which buildings are you reporting for?</Typography>
          <Typography variant='caption' color='text.secondary'>
            Select all buildings within this district you&apos;ll be completing this report for.
          </Typography>
          <TextField
            select
            fullWidth
            size='small'
            value={selectedBuildingIds}
            onChange={event => {
              const value = event.target.value as unknown as number[]
              setSelectedBuildingIds(typeof value === 'string' ? [] : value)
            }}
            sx={{ maxWidth: '480px', mt: '8px' }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected: unknown): JSX.Element => {
                const ids = selected as number[]
                if (ids.length === 0) return <Typography component='span' color='text.secondary'>Select Building(s)</Typography>
                return <>{district.buildings.filter(b => ids.includes(b.organizationBuildingId)).map(b => b.name).join(', ')}</>
              }
            }}
          >
            {district.buildings.map(building => (
              <MenuItem key={building.organizationBuildingId} value={building.organizationBuildingId} data-testid={`p2pBuildingOption-${building.organizationBuildingId}`}>
                <Checkbox checked={selectedBuildingIds.includes(building.organizationBuildingId)} />
                <ListItemText primary={building.name} />
              </MenuItem>
            ))}
          </TextField>
        </Box>}

      <WizardFooter
        onBack={onBack}
        onContinue={() => { if (district != null) onContinue(district.districtOrganizationId, selectedBuildingIds) }}
        continueDisabled={district == null || selectedBuildingIds.length === 0}
        backTestId='p2pGettingStartedBack'
        continueTestId='p2pGettingStartedContinue'
      />
    </Box>
  )
}
